import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { User, Briefcase, FileText, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ReviewStepProps {
  formData: {
    contactDetails: {
      fullName: string;
      email: string;
      phone: string;
    };
    experience: {
      type: string;
      cvFile: File | null;
      pastedText: string;
      workHistory: Array<{
        jobTitle: string;
        company: string;
        duration: string;
        description: string;
      }>;
    };
    skillsText: string;
  };
  job: {
    title: string;
    company: string;
  };
  onBack: () => void;
  onSubmit: () => void;
}

export const ReviewStep = ({ formData, job, onBack, onSubmit }: ReviewStepProps) => {
  const [confirmed, setConfirmed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = () => {
    if (!confirmed) {
      toast({
        title: "Confirmation Required",
        description: "Please confirm that your information is accurate before submitting.",
        variant: "destructive",
      });
      return;
    }

    onSubmit();
  };

  return (
    <>
      <CardHeader>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-6 w-6 text-primary" />
          <CardTitle className="text-2xl">Ready to send?</CardTitle>
        </div>
        <p className="text-muted-foreground">
          Review your application one last time before sending it to {job.company}.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pb-8">
        {/* Contact Details Summary */}
        <div className="bg-muted/30 p-5 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Contact Details</h3>
          </div>
          <div className="space-y-1 text-sm">
            <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{formData.contactDetails.fullName}</span></p>
            <p><span className="text-muted-foreground">Email:</span> <span className="font-medium">{formData.contactDetails.email}</span></p>
            <p><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{formData.contactDetails.phone}</span></p>
          </div>
        </div>

        {/* Experience Summary */}
        <div className="bg-muted/30 p-5 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Your Experience</h3>
          </div>
          <div className="text-sm">
            {formData.experience.type === "upload" && formData.experience.cvFile && (
              <p className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{formData.experience.cvFile.name}</span>
                <span className="text-muted-foreground">
                  ({(formData.experience.cvFile.size / 1024).toFixed(0)} KB)
                </span>
              </p>
            )}

            {formData.experience.type === "build" && formData.experience.workHistory.length > 0 && (
              <div className="space-y-3">
                {formData.experience.workHistory.map((entry, index) => (
                  <div key={index} className="border-l-2 border-primary pl-3">
                    <p className="font-medium">{entry.jobTitle}</p>
                    <p className="text-muted-foreground">{entry.company}</p>
                    <p className="text-xs text-muted-foreground">{entry.duration}</p>
                  </div>
                ))}
              </div>
            )}

            {formData.experience.type === "paste" && formData.experience.pastedText && (
              <p className="text-muted-foreground line-clamp-3">
                {formData.experience.pastedText}
              </p>
            )}
          </div>
        </div>

        {/* Skills Summary */}
        <div className="bg-muted/30 p-5 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Why You're a Good Fit</h3>
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {formData.skillsText}
          </p>
        </div>

        {/* Confirmation Checkbox */}
        <div className="bg-primary/5 p-5 rounded-lg border border-primary/20">
          <div className="flex items-start gap-3">
            <Checkbox
              id="confirm"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked as boolean)}
              className="mt-1"
            />
            <label
              htmlFor="confirm"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I confirm that the information I have provided is accurate to the best of my knowledge.
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1">
            Back to Edit
          </Button>
          <Button 
            onClick={handleSubmit} 
            className="flex-1"
            disabled={!confirmed}
          >
            Submit My Application
          </Button>
        </div>
      </CardContent>
    </>
  );
};
